import { getI18n } from 'react-i18next'
import {
  createWorkOrderPresenter,
  presentationFieldCost,
  type PresentationField,
  type WorkOrderPresenter,
} from './workOrderPresentation'
import { createChatPresentationTransport } from './chatPresentationTransport'
import { alreadyInDisplayLanguage, catalogDisplayText } from './chatDisplayLanguage'
import { createChatDisplayCache } from './chatDisplayCache'
import { createChatDisplayRegistry } from './chatDisplayRegistry'
import { useAuthStore } from '@/stores/authStore'
import { admitChatPresentation } from './chatPresentationAdmission'
import { reportPresentation } from './presentationDiagnostics'

function protectedField(
  field: PresentationField,
  names: string[],
  exactIdentities: string[],
): PresentationField {
  if (field.kind !== 'text') return field
  const literals =
    field.value.match(
      /\b\d+(?:[.,]\d+)?\s*(?:A|V|kW|W|Hz|rpm|mm|cm|kg|g|ml|mL|Nm|bar|psi|L|EA)\b|\b\d+[.,]\d+\b|\b[A-Z]{2,}[-/][A-Z0-9.-]+\b/g,
    ) ?? []
  const markup =
    field.value.match(
      /https?:\/\/\S+|```[\s\S]*?```|`[^`\n]+`|\[(?:doc|wo|norma|equip):[^\]]+\]|\]\([^)]*\)|\[|(?:^|\n)[ \t]*(?:#{1,6} |> |[-+*] |\d+\. )|[*_~]{1,3}|\||\n/g,
    ) ?? []
  return {
    ...field,
    protectedValues: [...names, ...literals, ...markup, ...(field.protectedValues ?? [])],
    protectedTokenValues: [...exactIdentities, ...(field.protectedTokenValues ?? [])],
  }
}

interface DisplayResultArgs {
  field: Parameters<WorkOrderPresenter['resolve']>[0]
  locale: string
  t: Parameters<WorkOrderPresenter['resolve']>[2]
  names: string[]
  exactIdentities: string[]
  displayed: ReturnType<typeof createChatDisplayCache>
  admitted: ReturnType<typeof createAdmittedResolver>
  signal: AbortSignal
}

/** Original / catalog / already-in-language / translated, in that precedence. */
async function displayResult({ field, locale, t, names, exactIdentities, displayed, admitted, signal }: DisplayResultArgs) {
  const i18n = getI18n()
  const known =
    displayed.get(locale, field.value) ??
    (field.kind === 'text' && i18n ? catalogDisplayText(i18n, field.value, locale) : undefined)
  return field.kind === 'identity' ||
    names.includes(field.value) ||
    exactIdentities.includes(field.value)
    ? { text: field.value, state: 'original' as const }
    : known !== undefined
      ? { text: known, state: 'translated' as const }
      : field.kind === 'text' && (await alreadyInDisplayLanguage(field.value, locale).catch(() => false))
        ? { text: field.value, state: 'original' as const }
        : await admitted.resolve(protectedField(field, names, exactIdentities), locale, t, signal)
}

interface ResolveScope {
  isDisposed: () => boolean
  names: string[]
  exactIdentities: string[]
  displayed: ReturnType<typeof createChatDisplayCache>
  registry: ReturnType<typeof createChatDisplayRegistry>
  admitted: ReturnType<typeof createAdmittedResolver>
}

const resolveInScope = async (
  scope: ResolveScope,
  ...[field, locale, t]: Parameters<WorkOrderPresenter['resolve']>
): ReturnType<WorkOrderPresenter['resolve']> => {
  const { names, exactIdentities, displayed, registry, admitted } = scope
  if (scope.isDisposed()) {
    reportPresentation({ event: 'unavailable', stage: 'display', reason: 'scope_disposed', text: field.value })
    return { text: field.value, state: 'unavailable' }
  }
  const signal = admitted.signal()
  registry.record(field, locale)
  const result = await displayResult({ field, locale, t, names, exactIdentities, displayed, admitted, signal })
  if (signal.aborted) {
    reportPresentation({ event: 'unavailable', stage: 'display', reason: 'admission_signal_aborted', text: field.value })
    return { text: field.value, state: 'unavailable' }
  }
  if (result.state !== 'unavailable') displayed.set(locale, field.value, result.text)
  return result
}

/** Cached strings are presentation-only and belong to one authenticated scope. */
export function createChatDisplayPresenter(
  owner: { tenantId: string; userId: string },
  names: string[],
  exactIdentities: string[] = [],
) {
  const transport = createChatPresentationTransport(owner)
  const presenter = createWorkOrderPresenter({
    translate: transport.translate,
    timeoutMs: 120_000,
    cacheSize: 3000,
    diagnose: (reason, value) =>
      reportPresentation({ event: 'unavailable', stage: 'presenter', reason, text: value }),
    discard: transport.forget,
  })
  const displayed = createChatDisplayCache()
  const registry = createChatDisplayRegistry()
  const admitted = createAdmittedResolver(presenter, JSON.stringify([owner.tenantId, owner.userId]), transport.hasCached)
  let leases = 0
  let disposed = false
  let inFlight = 0
  const dispose = () => {
    if (!disposed) reportPresentation({ event: 'scope_disposed', inFlight })
    disposed = true
    admitted.clear()
    transport.dispose()
    displayed.clear()
    registry.clear()
  }
  const resolve: WorkOrderPresenter['resolve'] = async (field, locale, t) => {
    inFlight++
    try {
      return await resolveField(field, locale, t)
    } finally {
      inFlight--
    }
  }
  const scope: ResolveScope = { isDisposed: () => disposed, names, exactIdentities, displayed, registry, admitted }
  const resolveField: WorkOrderPresenter['resolve'] = (field, locale, t) => resolveInScope(scope, field, locale, t)
  return {
    ...presenter,
    clear: admitted.clear,
    resolve,
    present: ((field, locale, t) => {
      const text = displayed.get(locale, field.value)
      return text === undefined
        ? presenter.present(protectedField(field, names, exactIdentities), locale, t)
        : { text, state: 'translated' }
    }) as WorkOrderPresenter['present'],
    dispose,
    getRevision: displayed.getRevision,
    subscribe: displayed.subscribe,
    isCurrent: () => {
      const current = useAuthStore.getState()
      return !disposed && current.tenantId === owner.tenantId && current.userId === owner.userId
    },
    resolveDisplayed(
      locale: string,
      t: Parameters<WorkOrderPresenter['resolve']>[2],
      signal?: AbortSignal,
    ) {
      return registry.resolve(locale, t, this.resolve, signal)
    },
    retain: () => {
      leases++
    },
    release: () => {
      leases--
      queueMicrotask(() => {
        if (!leases) dispose()
      })
    },
  }
}

/** Keep detector work bound to the generation that requested it, including clear(). */
function createAdmittedResolver(presenter: WorkOrderPresenter, ownerKey: string, cached: (text: string, locale: string) => boolean) {
  let admission = new AbortController()
  const pending = new Map<string, ReturnType<WorkOrderPresenter['resolve']>>()
  return {
    signal: () => admission.signal,
    clear() {
      admission.abort()
      admission = new AbortController()
      pending.clear()
      presenter.clear()
    },
    resolve(field: PresentationField, locale: string, t: Parameters<WorkOrderPresenter['resolve']>[2], signal: AbortSignal) {
      if (signal.aborted) return Promise.resolve({ text: field.value, state: 'unavailable' as const })
      if (field.kind !== 'text' || presenter.present(field, locale, t).state !== 'pending')
        return presenter.resolve(field, locale, t)
      const key = JSON.stringify([locale, field])
      const existing = pending.get(key)
      if (existing) return existing
      const cost = presentationFieldCost(field)
      if (cached(cost.text, locale)) return presenter.resolve(field, locale, t)
      const result = admitChatPresentation(ownerKey, locale, cost, signal,
        () => presenter.resolve(field, locale, t))
        .catch((error: unknown) => {
          reportPresentation({
            event: 'unavailable',
            stage: 'admission',
            reason: error instanceof Error ? error.message.slice(0, 40) : 'unknown',
            text: field.value,
          })
          return { text: field.value, state: 'unavailable' as const }
        })
        .finally(() => { if (pending.get(key) === result) pending.delete(key) })
      pending.set(key, result)
      return result
    },
  }
}
